import { useState } from "react";
import Cross from "../../assets/icons/Cross";
import DefaultButton from "../default-button/DefaultButton";
import InputField from "../input-field/InputField";
import ArrowLeft from "../../assets/icons/ArrowLeft";
import Tick from "../../assets/icons/Tick";
import DatePicker from "react-datepicker";
import { CustomDateInput } from "../CustomDateInput";
import { timeSlots } from "../../misc/constants";
import { Switch } from "../Switch";
import "react-datepicker/dist/react-datepicker.css";

interface CreateLessonModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (lessonData: LessonFormData) => void;
}

export interface LessonFormData {
    cabinet: string;
    subjectName: string;
    description: string;
    lessonType: 'lesson' | 'exam';
    date: Date | null;
    startTime: string;
    endTime: string;
}

export default function CreateLessonModal({
    isOpen,
    onClose,
    onSubmit
}: CreateLessonModalProps) {
    const [formData, setFormData] = useState<LessonFormData>({
        cabinet: '',
        subjectName: '',
        description: '',
        lessonType: 'lesson',
        date: null,
        startTime: '',
        endTime: '',
    });

    const handleInputChange = (field: keyof LessonFormData) => (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setFormData(prev => ({
            ...prev,
            [field]: e.target.value
        }));
    };

    const handleLessonTypeChange = (type: string) => () => {
        setFormData(prev => ({
            ...prev,
            lessonType: type as 'lesson' | 'exam'
        }));
    };

    const handleDateChange = (date: Date | null) => {
        setFormData(prev => ({
            ...prev,
            date
        }));
    };

    const handleStartTimeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setFormData(prev => ({
            ...prev,
            startTime: e.target.value
        }));
    };

    const handleEndTimeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setFormData(prev => ({
            ...prev,
            endTime: e.target.value
        }));
    };

    const handleSubmit = () => {
        if (validateForm()) {
            onSubmit(formData);
            handleClose();
        }
    };

    const handleClose = () => {
        // Сброс формы при закрытии
        setFormData({
            cabinet: '',
            subjectName: '',
            description: '',
            lessonType: 'lesson',
            date: null,
            startTime: '',
            endTime: '',
        });
        onClose();
    };

    const validateForm = (): boolean => {
        return Boolean(
            formData.cabinet &&
            formData.subjectName &&
            formData.date &&
            formData.startTime &&
            formData.endTime &&
            timeSlots.indexOf(formData.endTime) > timeSlots.indexOf(formData.startTime)
        );
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="p-8 rounded-xl bg-white text-black absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col max-w-2xl w-full">
                <div>
                    <div
                        onClick={handleClose}
                        className="flex justify-end cursor-pointer"
                    >
                        <Cross additionalStyle="text-[#5F6B88]" />
                    </div>
                    <p className="text-black font-normal text-[24px]">
                        Создание урока
                    </p>
                </div>

                <div className="flex-grow mt-8 flex flex-col gap-6">
                    {/* Кабинет */}
                    <div className="flex flex-col">
                        <p className="font-medium text-[15px] text-[#5F6B88] mb-2">
                            Кабинет
                        </p>
                        <input
                            type="text"
                            value={formData.cabinet}
                            onChange={handleInputChange('cabinet')}
                            className="bg-[#F0F3FA] w-full resize-none outline-none rounded-lg px-4 py-3"
                            placeholder="Введите номер кабинета"
                        />
                    </div>

                    {/* Название предмета */}
                    <div className="flex flex-col">
                        <p className="font-medium text-[15px] text-[#5F6B88] mb-2">
                            Название предмета
                        </p>
                        <input
                            type="text"
                            value={formData.subjectName}
                            onChange={handleInputChange('subjectName')}
                            className="bg-[#F0F3FA] w-full resize-none outline-none rounded-lg px-4 py-3"
                            placeholder="Введите название предмета"
                        />
                    </div>

                    {/* Описание урока */}
                    <div className="flex flex-col">
                        <p className="font-medium text-[15px] text-[#5F6B88] mb-2">
                            Описание урока
                        </p>
                        <textarea
                            value={formData.description}
                            onChange={handleInputChange('description')}
                            className="bg-[#F0F3FA] w-full resize-none outline-none rounded-lg px-4 py-3 h-32"
                            placeholder="Введите описание урока"
                        />
                    </div>

                    {/* Сваппер типа урока */}
                    <div className="flex flex-col">
                        <Switch
                            options={[
                                { label: "Провести урок", value: "lesson" },
                                { label: "Провести экзамен", value: "exam" }
                            ]}
                            value={formData.lessonType}
                            handleChange={handleLessonTypeChange}
                            wrapperClassName="flex flex-col"
                            switchClassName="!mt-2"
                            labelClassName="!text-[#5F6B88] !font-medium !text-[15px] !mb-2"
                            id="lesson-type-switch"
                            label="Тип урока"
                        />
                    </div>

                    {/* Дата, начало и окончание */}
                    <div className="flex flex-row gap-3">
                        <div className="flex flex-col flex-1">
                            <p className="font-medium text-[15px] text-[#5F6B88] mb-2">
                                Дата
                            </p>
                            <DatePicker
                                selected={formData.date}
                                onChange={handleDateChange}
                                customInput={<CustomDateInput hasTime={false} />}
                                dateFormat="dd/MM/yyyy"
                            />
                        </div>
                        <div className="flex flex-col flex-1">
                            <p className="font-medium text-[15px] text-[#5F6B88] mb-2">
                                Начало
                            </p>
                            <select
                                value={formData.startTime}
                                onChange={handleStartTimeChange}
                                className="bg-[#F0F3FA] w-full resize-none outline-none rounded-lg px-4 py-3"
                            >
                                <option value="">Выберите время</option>
                                {timeSlots.map(time => (
                                    <option key={time} value={time}>{time}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex flex-col flex-1">
                            <p className="font-medium text-[15px] text-[#5F6B88] mb-2">
                                Окончание
                            </p>
                            <select
                                value={formData.endTime}
                                onChange={handleEndTimeChange}
                                className="bg-[#F0F3FA] w-full resize-none outline-none rounded-lg px-4 py-3"
                            >
                                <option value="">Выберите время</option>
                                {timeSlots.map(time => (
                                    <option key={time} value={time}>{time}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Кнопки */}
                <div className="grid grid-cols-[5fr_7fr] gap-3 mt-4">
                    <DefaultButton
                        buttonText="Вернуться"
                        additionalStyle="bg-[#5F6B88] text-white px-8 py-3 text-nowrap"
                        Icon={ArrowLeft}
                        onClick={handleClose}
                    />
                    <DefaultButton
                        buttonText="Создать урок"
                        additionalStyle={`bg-valley-dark-green text-white px-8 py-3 text-nowrap ${
                            !validateForm() ? 'opacity-30' : ''
                        }`}
                        Icon={Tick}
                        onClick={handleSubmit}
                        disabled={!validateForm()}
                    />
                </div>
            </div>
        </div>
    );
}

